// ==== public/shared/protocol.js ====
'use strict';

// ============================================================================
// Powder Party — wire contract shared by display and controllers.
// GAME-SIDE config (not part of the partyplug kit): the relay/STUN deployment
// URLs and this game's message vocabulary live here and are injected into the
// kit at construction. The kit reads none of these globals.
//
// The game: a downhill ski race. Phones are controllers — tucked-and-fast is the
// default; tilt to CARVE, hold to BRAKE (sit up for control), and flick in the air
// to FLIP (ramps auto-launch you). The shared screen renders the authoritative
// slope simulation; first skier to the bottom wins.
// ============================================================================

// Party-Server relay URL (signaling + game-event fallback).
var RELAY_URL = 'wss://ws.paperballoon.games';

// STUN server for the WebRTC fastlane (server-reflexive candidates so
// cross-network peers connect). STUN is UDP and ignored by CSP connect-src.
var STUN_URL = 'stun:stun.paperballoon.games:3478';

// Message types carried inside the Party-Server `data` field. Every message is
// a plain object with a `.type` drawn from here.
var MSG = {
  // Controller -> Display
  HELLO: 'hello',                     // {name?, rejoinToken?} sent right after join — rejoinToken claims a dropped seat (cross-device reconnect, from the big screen's QR ?claim=)
  // CONTROL — the hot path (~25Hz, fastlane). All fields are latest-wins safe:
  //   s : carve  [-1,1]  gyro roll (or air-lean while airborne). 0 = straight.
  //   t : tuck   0|1     DEFAULT 1 (tucked/fast); 0 only while BRAKING (held).
  //   j : jumpSeq 0..255  wrapping up-flick edge. Does nothing on the snow now
  //                       ("flick up to jump" was removed — ramps auto-launch). In
  //                       the AIR it's a back-flip fallback for inputs without an
  //                       analog f (keyboard/bots); real controllers send f's angle.
  //   f : { n:0..255, a, m } wrapping ANALOG AIR-trick flick. a = flick angle
  //                       (rad, up = +π/2): up→back flip, down→front, sides→spin,
  //                       diagonals→cork. m = strength 0..1 → spin rate. Air-only
  //                       (ignored on the snow). Like j, the display fires one
  //                       action per CHANGE, so a dropped fastlane frame re-delivers.
  CONTROL: 'control',
  START_GAME: 'start_game',           // host only — from the lobby starts a fresh SERIES; from the final board starts a NEW series (mid-series there's no button — the display auto-advances)
  SET_LEVEL: 'set_level',             // {level} host picks the run difficulty (Blue/Red/Black) in the lobby — display validates + echoes LEVEL_UPDATE
  SET_RUNS: 'set_runs',               // {runs} host picks how many runs the series is (RUN_COUNTS) in the lobby — display validates + echoes RUNS_UPDATE
  RETURN_TO_LOBBY: 'return_to_lobby', // "New game" — abort the series back to the lobby (host)
  PAUSE_GAME: 'pause_game',           // request a pause (any player, mid-countdown/run)
  RESUME_GAME: 'resume_game',         // request resume from the pause overlay
  LEAVE: 'leave',                     // intentional exit (back-out) — display frees the seat at once (no reconnect QR)
  SET_NAME: 'set_name',               // {name} live rename (the Paper Balloon launcher's name sheet via PaperBalloonHost.editName()) — display clamps like HELLO's name and republishes the roster
  PING: 'ping',

  // Display -> specific controller
  WELCOME: 'welcome',                 // {peerIndex, colorIndex, hostPeerIndex, roomState, players, inRun, level, runs, standings?, paused?}
                                      // level = the room's current run difficulty (Blue/Red/Black) so a joiner's lobby selector lands on the right tier (a MISSING level reads as DEFAULT_LEVEL).
                                      // runs = the series length the host has picked (RUN_COUNTS) so a joiner's lobby selector lands on it (a MISSING runs reads as DEFAULT_RUNS).
                                      // inRun=false mid-run = no live skier (late joiner / expired seat) — the phone
                                      // parks on its "run in progress" screen; a MISSING flag reads as true (an older
                                      // display that never stamps it must not strand its rejoiners off the pad).
                                      // During RESULTS, standings carries the
                                      // final STANDINGS payload so the phone lands on the board, not the lobby; a
                                      // paused run stamps paused=true so a rejoiner shows the pause overlay.
  ROOM_FULL: 'room_full',             // join refused — no free seat (room full, or every seat held for a reconnect)
  LOBBY_UPDATE: 'lobby_update',       // roster/host snapshot {hostPeerIndex, players}. Not sent as a message anymore:
                                      // the display publishes it as the relay's RETAINED room snapshot (set_state, one
                                      // publish instead of an N-recipient fanout; replayed to every (re)joiner), and
                                      // ControllerNet re-shapes the snapshot onto this envelope for the apply path.
  PLAYER_STATE: 'player_state',       // {position, of, progress[0..1], airborne, finished} — light HUD feed (~6.5 Hz, see main.js HUD_HZ_MS)
  PONG: 'pong',

  // Display -> all controllers (broadcast)
  LEVEL_UPDATE: 'level_update',       // {level} authoritative run difficulty changed (host picked) — phones repaint the selector. NOT a RUN_STATE msg: a parked late joiner still gets it (it's room config, like LOBBY_UPDATE). Joiners also read the current level off WELCOME.
  RUNS_UPDATE: 'runs_update',         // {runs} authoritative series length changed (host picked, lobby only) — phones repaint the selector. Like LEVEL_UPDATE it's room config, not RUN_STATE, so a parked late joiner still gets it. Joiners also read it off WELCOME.
  COUNTDOWN: 'countdown',             // {n} 3..2..1..GO
  GAME_START: 'game_start',
  STANDINGS: 'standings',             // {over, seriesOver, runIndex, runTotal, hostPeerIndex, total, order:[{playerId,name,colorIndex,ai,finished,time,dnf,points,score,champion}]}
                                      // pushed as each skier finishes (over=false) + at run end (over=true).
                                      // runIndex/runTotal = which run of the series this is (1-based) and how many in all.
                                      // points = this run's series points for the row (see seriesPoints); score = cumulative across the series.
                                      // seriesOver = the final run is done — the order is sorted by cumulative score and champion=true marks the leader(s).
                                      // Late joiners (seated, not in this run) trail the order as unranked
                                      // {newPlayer:true} rows — queued for the next run, not results
  INTERMISSION: 'intermission',       // {n, runIndex, runTotal} between-runs countdown tick — the display auto-advances the series; phones show "Next run in n…". NOT a RUN_STATE msg (it fires during RESULTS, where the parked-run gate is already cleared).
  GAME_END: 'game_end',               // return-to-lobby signal (no payload); controllers go back to the lobby
  GAME_PAUSED: 'game_paused',         // run frozen — controllers show the pause overlay
  GAME_RESUMED: 'game_resumed',       // run resumed — controllers hide the pause overlay
  DISPLAY_CLOSED: 'display_closed'    // big screen closing for good (pagehide goodbye) — controllers
                                      // bail straight to the device chooser, no display-gone grace wait
};

// Message types that ride the low-latency WebRTC fastlane (unreliable,
// unordered, latest-wins). Only idempotent, latest-state-wins inputs belong
// here. All other traffic and WS fallback flow through the relay.
var FASTLANE_TYPES = { control: true };

// RUN-STATE broadcasts — messages that repaint a phone for the CURRENT run.
// A controller parked on its "run in progress" screen (late joiner) drops
// exactly these; room-management traffic (WELCOME, LOBBY_UPDATE, STANDINGS,
// GAME_END, DISPLAY_CLOSED) stays live because it's what releases that state.
// Add new run-only broadcasts HERE and the controller's gate picks them up.
var RUN_STATE_MSGS = {
  [MSG.COUNTDOWN]: true,
  [MSG.GAME_START]: true,
  [MSG.PLAYER_STATE]: true,
  [MSG.GAME_PAUSED]: true,
  [MSG.GAME_RESUMED]: true
};

// Relay-level join refusals, matched VERBATIM by the controller — these exact
// strings are the relay's wire contract (the sibling games match the same
// literals). An unrecognized error message still surfaces inline as raw text.
var RELAY_ERRORS = {
  ROOM_NOT_FOUND: 'Room not found',
  ROOM_FULL: 'Room is full'
};

// Room states (must match partyplug RoomFlow.STATES — keep in sync by hand;
// the controller page never loads RoomFlow, so the values live here too).
var ROOM_STATE = {
  LOBBY: 'lobby',
  COUNTDOWN: 'countdown',
  PLAYING: 'playing',
  RESULTS: 'results'
};

// ---- Game constants (shared so display + controller agree) ----
// Human seats per room. A short-handed lobby is topped up with AI ("CPU")
// skiers on the display side, so this caps PHONES, not skiers in a run.
var MAX_PLAYERS = 4;
var COUNTDOWN_SECONDS = 3;

// A run is one race; a SERIES is N runs head-to-head with points accumulating to
// an overall champion. The host picks the length in the lobby from these presets
// (a segmented switch like the difficulty selector). DEFAULT_RUNS is the
// pre-selected one. Series state is owned by the authoritative display.
var RUN_COUNTS = [3, 5, 7];
var DEFAULT_RUNS = 5;
var INTERMISSION_SECONDS = 5; // breather between runs (the board shows the scores) before the next auto-starts

// Series points a finishing place earns this run. Linear by place over the field:
// 1st = fieldSize, last finisher = 1, a DNF (didn't cross) earns 0. With the
// always-4 field that's 4/3/2/1. `rank` is the engine's 1-based finishing rank
// (finishers by time, then DNFs); `fieldSize` is the number of skiers in the run.
// Pure + side-effect-free so both the display (authoritative tally) and the Node
// tests share one definition. The whole field — humans AND CPU — is scored; the
// overall champion is simply whoever has the most.
function seriesPoints(rank, fieldSize, finished) {
  if (!finished) return 0;
  return Math.max(0, fieldSize - rank + 1);
}

// Run difficulty tiers (the piste-grading colours), host-selectable in the lobby.
// A tier only changes the procedural MOUNTAIN — slope shape + obstacle/jump
// density (see LEVEL_TUNING in shared/slopes.js) — never the physics or the AI.
// id+label drive the controller's selector; color tints the active segment (it
// mirrors the matching SKIER_COLORS / theme livery). The ORDER is easy→hard. The
// tier ids must stay in sync with LEVEL_TUNING in slopes.js (that file is THREE-
// and protocol-free for the Node tests, so the list lives in both by hand).
var LEVELS = [
  { id: 'blue',  label: 'Blue',  color: '#2d9cdb' },  // gentle, wide, sparse — a cruiser (the DEFAULT)
  { id: 'red',   label: 'Red',   color: '#e6492d' },  // steeper, narrower (the original mountain)
  { id: 'black', label: 'Black', color: '#26313f' },  // steep, narrow, a denser minefield
];
var DEFAULT_LEVEL = 'blue';

// Skier-suit livery palette, indexed by the dense color slot
// RoomFlow.lowestFreeSlot hands out. Both sides resolve colorIndex → hex. This
// is the only thing that distinguishes players (every skier handles the same).
// (Mirrors the --car-* tokens in shared/theme.css.)
var SKIER_COLORS = [
  '#e6492d', // red
  '#f2b134', // amber
  '#2bb673', // green
  '#2d9cdb', // blue
  '#9b51e0', // purple
  '#eb5e9c', // pink
  '#f2784b', // orange
  '#56ccf2'  // cyan
];

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    MSG, FASTLANE_TYPES, RUN_STATE_MSGS, RELAY_ERRORS, ROOM_STATE,
    RELAY_URL, STUN_URL,
    MAX_PLAYERS, COUNTDOWN_SECONDS,
    RUN_COUNTS, DEFAULT_RUNS, INTERMISSION_SECONDS, seriesPoints,
    SKIER_COLORS, LEVELS, DEFAULT_LEVEL
  };
}

// ==== partyplug/PartyConnection.js ====
'use strict';

/**
 * PartyConnection — WebSocket wrapper for Party-Server relay protocol.
 *
 * Public peers are addressed by numeric slot index (0, 1, 2, …). The room
 * creator is always index 0. Indices are stable for the room's lifetime and
 * never reassigned. clientId is supplied on create/join as a per-slot bearer
 * secret — the relay matches it to the slot for reconnect — and never crosses
 * the wire in any other direction. Callers must keep clientId private (don't
 * embed it in app messages, URLs, or logs).
 *
 * The relay requires a clientId, so one is auto-generated when the caller omits
 * it. An auto-generated id is stable for the lifetime of this instance (so
 * in-session reconnects land on the same slot) but NOT across page reloads — a
 * game that wants reconnect to survive a reload should persist a clientId
 * (e.g. in localStorage) and pass it in.
 *
 * Party-Server protocol:
 *   Client → PS:  create { clientId, maxClients, url? }
 *   Client → PS:  join   { clientId, room }
 *   Client → PS:  send   { data, to? }            // to is a peer index (number)
 *   PS → Client:  created      { room, index: 0, instance?, region?, url? }
 *   PS → Client:  joined       { room, index, peers: number[], url? }
 *   PS → Client:  peer_joined  { index }
 *   PS → Client:  peer_left    { index }
 *   PS → Client:  message      { from, data }     // from is a peer index (number)
 *   PS → Client:  error        { message }
 */
class PartyConnection {
  constructor(relayUrl, options) {
    this.relayUrl = relayUrl;
    // The relay requires a clientId; generate a session-stable one if omitted.
    this.clientId = (options && options.clientId) || PartyConnection._genClientId();
    this.ws = null;
    this._reconnectTimer = null;
    this._shouldReconnect = true;
    this.maxReconnectAttempts = (options && options.maxReconnectAttempts) || 5;
    this.reconnectAttempt = 0;

    // Callbacks
    this.onOpen = null;        // () => void
    this.onClose = null;       // (attempt: number, maxAttempts: number, meta?: {replaced?: boolean, roomClosed?: boolean}) => void
    this.onError = null;       // () => void
    this.onMessage = null;     // (from: number, data: object) => void
    this.onProtocol = null;    // (type: string, msg: object) => void
    this.onState = null;       // (data: any) => void, retained host snapshot
  }

  // Random, opaque clientId. crypto.randomUUID where available (browsers,
  // Node 16+); a Math.random/time fallback otherwise. Not security-grade, just
  // unique enough to key a relay slot.
  static _genClientId() {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return 'pc-' + crypto.randomUUID();
      }
    } catch (e) { /* fall through */ }
    return 'pc-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);
  }

  connect() {
    this._discardOldWs();

    this._shouldReconnect = true;
    var ws = new WebSocket(this.relayUrl);
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws) return; // stale
      if (this.onOpen) this.onOpen();
    };

    ws.onmessage = (event) => {
      if (this.ws !== ws) return; // stale
      var msg;
      try { msg = JSON.parse(event.data); } catch (_) { return; }

      if (msg.type === 'message') {
        if (this.onMessage) this.onMessage(msg.from, msg.data);
      } else if (msg.type === 'state') {
        // Retained host snapshot, replayed right after `joined` on (re)join
        // and pushed live on each host update. Routed to onState (not
        // onProtocol) so a host that never sets onState (e.g. the display,
        // which authors state but doesn't consume it) silently ignores its
        // own replayed copy.
        if (this.onState) this.onState(msg.data);
      } else {
        if (this.onProtocol) this.onProtocol(msg.type, msg);
      }
    };

    ws.onclose = (event) => {
      if (this.ws !== ws) return; // stale — already replaced by reconnectNow
      if (event && event.code === 4000) {
        // Relay evicted us because another client joined with the same clientId
        this._shouldReconnect = false;
        if (this.onClose) this.onClose(0, 0, { replaced: true });
        return;
      }
      if (event && event.code === 4001) {
        // The room itself is gone (host sent close_room, or the relay's
        // hostless grace expired). Terminal: a reconnect would only bounce
        // off "Room not found".
        this._shouldReconnect = false;
        if (this.onClose) this.onClose(0, 0, { roomClosed: true });
        return;
      }
      this.reconnectAttempt++;
      if (this.onClose) this.onClose(this.reconnectAttempt, this.maxReconnectAttempts);
      if (this._shouldReconnect && this.reconnectAttempt <= this.maxReconnectAttempts) {
        this._scheduleReconnect();
      }
    };

    ws.onerror = () => {
      if (this.ws !== ws) return; // stale
      if (this.onError) this.onError();
    };
  }

  _discardOldWs() {
    if (this.ws) {
      var old = this.ws;
      this.ws = null;
      old.onopen = old.onmessage = old.onclose = old.onerror = null;
      try { old.close(); } catch (_) {}
    }
  }

  _scheduleReconnect() {
    clearTimeout(this._reconnectTimer);
    // Gentle backoff: 1s, 1.5s, 2.25s, 3.375s, capped at 5s
    var delay = Math.min(1000 * Math.pow(1.5, this.reconnectAttempt - 1), 5000);
    this._reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  _send(msg) {
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  // Create a room. `url` is an optional controller-URL template the relay
  // retains on the room and hands (with {room}/{instance} filled in) to anyone
  // who holds only the room code — in `created`/`joined` replies and via
  // GET /room/:code — so a code-only client can resolve which page to load.
  // The relay only accepts absolute https templates and rejects the whole
  // create on an invalid one, so callers must omit it rather than send a
  // non-https URL (e.g. a plain-http dev origin).
  create(maxClients, url) {
    var msg = { type: 'create', clientId: this.clientId, maxClients: maxClients };
    if (url) msg.url = url;
    this._send(msg);
  }

  join(room) {
    this._send({ type: 'join', clientId: this.clientId, room: room });
  }

  // Pin auto-reconnect to a specific relay shard. After the relay assigns an
  // instance (in the `created` reply), call this so reconnects rebuild the
  // sharded URL and land back on the same instance, instead of the bare
  // endpoint routing to whichever shard is least-loaded. The game supplies its
  // base relay URL; the kit owns the URL shape so games don't hand-build it.
  pinInstance(baseUrl, room, instance) {
    if (!instance) return;
    this.relayUrl = baseUrl + '/' + encodeURIComponent(room) + '?instance=' + encodeURIComponent(instance);
  }

  sendTo(to, data) {
    this._send({ type: 'send', data: data, to: to });
  }

  broadcast(data) {
    this._send({ type: 'send', data: data });
  }

  // Publish a retained state snapshot (host/slot-0 only; the relay rejects it
  // from anyone else). The relay keeps the latest blob on the room, pushes it
  // live to current peers (sender excluded), and replays it to any client right
  // after `joined` on (re)join. Costs exactly one broadcast; there is no silent
  // retain. `data` must be JSON-serializable and <= 16 KiB serialized.
  setState(data) {
    this._send({ type: 'set_state', data: data });
  }

  // Tear the room down for everyone (host/slot-0 only; the relay rejects it
  // from anyone else). The relay deletes the room, GET /room/:code turns 404
  // (killing stale rejoin links), and every member socket is closed with 4001,
  // which surfaces to them as onClose(0, 0, {roomClosed: true}). There is no
  // ack message: the sender's own 4001 close is the confirmation, unless the
  // caller close()s first (fine on pagehide, where the page is going away).
  closeRoom() {
    this._send({ type: 'close_room' });
  }

  reconnectNow() {
    clearTimeout(this._reconnectTimer);
    this.connect();
  }

  // Treat the current socket as a failed attempt and drive the normal
  // backoff/give-up path — for callers that detect failure themselves (e.g. a
  // socket that opened but the relay never answered create/join, which never
  // triggers onclose). Mirrors an unexpected onclose: discards the dead socket,
  // bumps the attempt counter, notifies onClose, and either schedules a backoff
  // reconnect or gives up once maxReconnectAttempts is passed.
  failAttempt() {
    if (!this._shouldReconnect) return;
    this._discardOldWs();
    this.reconnectAttempt++;
    if (this.onClose) this.onClose(this.reconnectAttempt, this.maxReconnectAttempts);
    if (this.reconnectAttempt <= this.maxReconnectAttempts) {
      this._scheduleReconnect();
    }
  }

  resetReconnectCount() {
    this.reconnectAttempt = 0;
  }

  close() {
    this._shouldReconnect = false;
    clearTimeout(this._reconnectTimer);
    this._discardOldWs();
  }

  get connected() {
    return this.ws && this.ws.readyState === 1;
  }
}

// Export for both Node.js and browser
if (typeof window !== 'undefined') {
  window.PartyConnection = PartyConnection;
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = PartyConnection;
}

// ==== partyplug/RoomFlow.js ====
'use strict';

// RoomFlow owns room state, roster identity/join order, presence, and host
// election. It does not own DOM, transport, countdown timers, or game fields
// like color/name/score; those are opaque data on the live player record.

(function (root, factory) {
  var RoomFlow = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = RoomFlow;
  } else {
    root.RoomFlow = RoomFlow;
  }
})(typeof self !== 'undefined' ? self : this, function () {

  var STATES = Object.freeze({
    LOBBY: 'lobby',
    COUNTDOWN: 'countdown',
    PLAYING: 'playing',
    RESULTS: 'results',
  });

  var VALID_TRANSITIONS = {};
  VALID_TRANSITIONS[STATES.LOBBY] = [STATES.COUNTDOWN];
  VALID_TRANSITIONS[STATES.COUNTDOWN] = [STATES.PLAYING, STATES.LOBBY];
  VALID_TRANSITIONS[STATES.PLAYING] = [STATES.RESULTS, STATES.LOBBY];
  VALID_TRANSITIONS[STATES.RESULTS] = [STATES.COUNTDOWN, STATES.LOBBY];

  function RoomFlow(opts) {
    opts = opts || {};
    // Optional () => peerIndex. When the transport designates a master
    // controller (AirConsole), supply it here. Returns null/undefined when
    // there is no platform master.
    this.masterProvider = typeof opts.masterProvider === 'function' ? opts.masterProvider : null;

    this.state = STATES.LOBBY;
    this.players = new Map();        // peerIndex -> player record
    this.hostPeerIndex = null;       // sticky host slot (raw; see `host` getter for effective)
    this._joinSeq = 0;               // monotonic joinedAt source (wall-clock ms collide within a tick)
    this._disconnected = new Set();  // peerIndices currently in the disconnect window
    this._order = [];                // active participants (snapshotted on COUNTDOWN, or via setActiveOrder)
    this._listeners = {};

    // Liveness (presence-timeout) decisions. Clock-free: every method is a pure
    // predicate the host calls with an injected nowMs. The detectors NEVER
    // mutate _disconnected and never _emit — the host applies a detected expiry
    // through the existing markDisconnected path, keeping the single-writer
    // invariant intact. opts.liveness?: { timeoutMs, graceMs, enabledProvider }.
    var liveness = opts.liveness || {};
    this._lastSeen = new Map();      // peerIndex -> last nowMs we heard from it
    this._livenessTimeoutMs = liveness.timeoutMs != null ? liveness.timeoutMs : Infinity;
    this._graceMs = liveness.graceMs || 0;
    // Optional () => boolean. When it returns false, liveness expiry is
    // suppressed (e.g. AirConsole, where the SDK owns connection tracking). Read
    // live so a host that sets it up after construction is detected correctly.
    this._livenessEnabledProvider =
      typeof liveness.enabledProvider === 'function' ? liveness.enabledProvider : null;
    this._graceDeadline = null;      // nowMs the late-joiner grace fires at, or null
  }

  RoomFlow.STATES = STATES;

  // Lowest free dense slot in [0, max). Pass slot values, not peerIndices, so
  // sparse transport ids (e.g. AirConsole device_id) don't become color/seat ids.
  RoomFlow.lowestFreeSlot = function (used, max) {
    var taken = used instanceof Set ? used : new Set(used);
    for (var i = 0; i < max; i++) { if (!taken.has(i)) return i; }
    return -1;
  };

  // ---- tiny event emitter (dependency-free; portable Node + browser) ----
  RoomFlow.prototype.on = function (type, handler) {
    (this._listeners[type] = this._listeners[type] || []).push(handler);
    var self = this;
    return function () { self.off(type, handler); };
  };
  RoomFlow.prototype.off = function (type, handler) {
    var arr = this._listeners[type];
    if (!arr) return;
    var i = arr.indexOf(handler);
    if (i >= 0) arr.splice(i, 1);
  };
  RoomFlow.prototype._emit = function (type, detail) {
    var arr = this._listeners[type];
    if (arr) { var copy = arr.slice(); for (var i = 0; i < copy.length; i++) copy[i](detail); }
    var wild = this._listeners['*'];
    if (wild) { var w = wild.slice(); for (var j = 0; j < w.length; j++) w[j](type, detail); }
  };

  // =====================================================================
  // Roster
  // =====================================================================

  // Add a player, or reconnect/refresh an existing one (same peerIndex).
  // `fields` is opaque game data merged onto the record (name, color slot,
  // level, ...). RoomFlow adds peerIndex/joinedAt/connected and never reads
  // the game fields. Returns the live player record.
  RoomFlow.prototype.addPlayer = function (peerIndex, fields) {
    fields = fields || {};
    var existing = this.players.get(peerIndex);
    if (existing) {
      var prevHost = this.host;
      // Reconnect: keep slot / joinedAt / host, refresh presence + fields.
      // Protect kit-owned fields against a caller passing them in `fields`
      // (e.g. a serialized record round-tripped through JSON): joinedAt is the
      // host-election tiebreak and peerIndex is the map key — neither may be
      // clobbered by game data.
      var savedJoinedAt = existing.joinedAt;
      Object.assign(existing, fields);
      existing.joinedAt = savedJoinedAt;
      existing.peerIndex = peerIndex;
      existing.connected = true;
      this._disconnected.delete(peerIndex);
      if (this.host !== prevHost) this._emit('hostchange', { hostPeerIndex: this.host });
      this._emit('playerupdate', { player: existing });
      this._emit('rosterchange', { players: this.list() });
      return existing;
    }
    var player = Object.assign({}, fields, {
      peerIndex: peerIndex,
      joinedAt: this._joinSeq++,
      connected: true,
    });
    this.players.set(peerIndex, player);
    // First joiner owns the sticky host slot. Also covers the "room emptied
    // then someone joined" case (hostPeerIndex was reset to null).
    if (this.hostPeerIndex == null) {
      this.hostPeerIndex = peerIndex;
      this._emit('hostchange', { hostPeerIndex: this.host });
    }
    this._emit('playerjoin', { player: player });
    this._emit('rosterchange', { players: this.list() });
    return player;
  };

  // Hard leave (peer_left). The sticky slot only moves when the holder
  // departs from LOBBY/RESULTS; a mid-game leave leaves the slot untouched
  // so a reconnecting host reclaims it (the `host` getter falls back
  // meanwhile). hostchange fires whenever the EFFECTIVE host changes — including
  // a mid-game departure where the sticky slot stays put but the getter's
  // fallback shifts to a present player.
  RoomFlow.prototype.removePlayer = function (peerIndex) {
    if (!this.players.has(peerIndex)) return;
    var prevHost = this.host;
    var wasHost = peerIndex === this.hostPeerIndex;
    this.players.delete(peerIndex);
    this._disconnected.delete(peerIndex);
    this._lastSeen.delete(peerIndex);
    var oi = this._order.indexOf(peerIndex);
    if (oi >= 0) this._order.splice(oi, 1);
    if (wasHost && (this.state === STATES.LOBBY || this.state === STATES.RESULTS)) {
      this.hostPeerIndex = this._electNextHost(peerIndex);
    }
    if (this.host !== prevHost) this._emit('hostchange', { hostPeerIndex: this.host });
    this._emit('playerleave', { peerIndex: peerIndex });
    this._emit('rosterchange', { players: this.list() });
  };

  // Re-key a player from one peerIndex to another. This is ONLY for cross-device
  // takeover: a different client (fresh clientId) claims a dropped player's
  // still-present slot and gets a new peerIndex from the relay. A same-client
  // reconnect keeps its index (the relay keys slots by clientId) and never needs
  // this. Preserves the record (incl. joinedAt) and rekeys host slot + order.
  RoomFlow.prototype.rekey = function (oldId, newId) {
    if (oldId === newId) return false;
    var rec = this.players.get(oldId);
    if (!rec) return false;
    var prevHost = this.host;
    this.players.delete(oldId);
    this.players.delete(newId); // drop the placeholder slot the returning peer got
    rec.peerIndex = newId;
    rec.connected = true;
    this.players.set(newId, rec);
    this._disconnected.delete(oldId);
    this._disconnected.delete(newId);
    // Keep the NEWER of the two last-seen stamps. The placeholder slot's stamp
    // (set when the claiming connection joined/HELLO'd) is the live signal — the
    // old seat's is up to a full reconnect-grace window stale, and carrying it
    // would let the very next liveness sweep expire the seat the instant it
    // reconnected. The old stamp survives only as a fallback for callers that
    // rekey a never-seen placeholder.
    var oldStamp = this._lastSeen.get(oldId);
    var newStamp = this._lastSeen.get(newId);
    this._lastSeen.delete(oldId);
    this._lastSeen.delete(newId);
    var kept = oldStamp == null ? newStamp
      : (newStamp == null ? oldStamp : Math.max(oldStamp, newStamp));
    if (kept != null) this._lastSeen.set(newId, kept);
    for (var i = 0; i < this._order.length; i++) {
      if (this._order[i] === oldId) this._order[i] = newId;
    }
    // The slot wasn't moved when this player blipped mid-game, so if it still
    // points at the old peerIndex, rekey it so the reconnecting host resumes.
    // Only the host's own slot is rekeyed; a non-host claim never promotes
    // (when there's no sticky host, the `host` getter's oldest-eligible
    // fallback already picks the right player).
    if (this.hostPeerIndex === oldId) {
      this.hostPeerIndex = newId;
    }
    if (this.host !== prevHost) this._emit('hostchange', { hostPeerIndex: this.host });
    this._emit('rosterchange', { players: this.list() });
    return true;
  };

  // Soft disconnect window (the player record stays; presence flips false).
  // Emits hostchange if the effective host shifts (e.g. the host blips mid-game
  // and the getter's fallback hands duty to a present player).
  RoomFlow.prototype.markDisconnected = function (peerIndex) {
    var p = this.players.get(peerIndex);
    if (!p) return;
    var prevHost = this.host;
    p.connected = false;
    this._disconnected.add(peerIndex);
    if (this.host !== prevHost) this._emit('hostchange', { hostPeerIndex: this.host });
    this._emit('rosterchange', { players: this.list() });
  };

  RoomFlow.prototype.markReconnected = function (peerIndex) {
    var p = this.players.get(peerIndex);
    if (!p) return;
    var prevHost = this.host;
    p.connected = true;
    this._disconnected.delete(peerIndex);
    if (this.host !== prevHost) this._emit('hostchange', { hostPeerIndex: this.host });
    this._emit('rosterchange', { players: this.list() });
  };

  // Clear every disconnect flag, marking all current players present. Used at
  // game start / lobby return where stale blip flags must not suppress host
  // eligibility for the new round.
  RoomFlow.prototype.clearDisconnected = function (nowMs) {
    // Re-stamp liveness on this "everyone present" transition (game start / lobby
    // return) so a controller that went quiet just before isn't instantly flagged
    // by expiredPeers() during COUNTDOWN (where the LOBBY gate no longer applies).
    // Callers pass nowMs; omitting it preserves the legacy clear-only behavior.
    if (nowMs != null) {
      for (var seenEntry of this.players) this._lastSeen.set(seenEntry[0], nowMs);
    }
    if (this._disconnected.size === 0) return;
    var prevHost = this.host;
    this._disconnected.clear();
    for (var entry of this.players) entry[1].connected = true;
    if (this.host !== prevHost) this._emit('hostchange', { hostPeerIndex: this.host });
    this._emit('rosterchange', { players: this.list() });
  };

  // =====================================================================
  // Host election (DisplayState.getHostPeerIndex / electNextHost / reconcile)
  // =====================================================================

  // During COUNTDOWN/PLAYING/RESULTS the candidate set is restricted to the
  // active participants (the `_order`), so a late joiner can't be handed host
  // duty for menu actions they can't reach. Open to everyone in LOBBY.
  RoomFlow.prototype._restricted = function () {
    return (this.state === STATES.COUNTDOWN ||
            this.state === STATES.PLAYING ||
            this.state === STATES.RESULTS) && this._order.length > 0;
  };

  RoomFlow.prototype._isEligible = function (peerIndex, eligibleSet) {
    return peerIndex != null &&
      this.players.has(peerIndex) &&
      !this._disconnected.has(peerIndex) &&
      (eligibleSet == null || eligibleSet.has(peerIndex));
  };

  // Oldest-joined present player within eligibleSet (null = everyone present),
  // optionally skipping excludeId. Shared by the `host` getter (no exclusion)
  // and _electNextHost (excludes the departing holder).
  RoomFlow.prototype._oldestEligible = function (eligibleSet, excludeId) {
    var bestId = null, bestJoin = Infinity;
    for (var entry of this.players) {
      var id = entry[0];
      if (id === excludeId) continue;
      if (this._disconnected.has(id)) continue;
      if (eligibleSet != null && !eligibleSet.has(id)) continue;
      var ja = entry[1].joinedAt == null ? Infinity : entry[1].joinedAt;
      if (ja < bestJoin) { bestJoin = ja; bestId = id; }
    }
    return bestId;
  };

  // Effective host: platform master (if eligible) -> sticky host (if
  // eligible) -> oldest-joined eligible present player. Read-only; the
  // sticky slot is only mutated by removePlayer / rekey / reconcile.
  Object.defineProperty(RoomFlow.prototype, 'host', {
    get: function () {
      var restricted = this._restricted();
      var eligible = restricted ? new Set(this._order) : null;
      if (this.masterProvider) {
        var m = this.masterProvider();
        if (this._isEligible(m, eligible)) return m;
      }
      if (this._isEligible(this.hostPeerIndex, eligible)) return this.hostPeerIndex;
      return this._oldestEligible(eligible);
    },
  });

  RoomFlow.prototype.isHost = function (peerIndex) {
    return peerIndex != null && peerIndex === this.host;
  };

  // Oldest-joined present player other than excludeId. Restricted to the
  // participant order while in COUNTDOWN/PLAYING/RESULTS, so committing the
  // sticky slot can never promote a late joiner (who is not in `_order`) over
  // the actual game participants. Unrestricted in LOBBY, where everyone present
  // is a valid candidate. Returns null when nobody qualifies.
  RoomFlow.prototype._electNextHost = function (excludeId) {
    return this._oldestEligible(this._restricted() ? new Set(this._order) : null, excludeId);
  };

  // Commit any pending sticky-host handoff. Called when entering LOBBY or
  // RESULTS (the moments host duty is actually exercised: Start, Play Again).
  RoomFlow.prototype._reconcileStickyHost = function () {
    if (this.players.size === 0) return;
    var eligible = this._restricted() ? new Set(this._order) : null;
    if (this.hostPeerIndex != null &&
        this.players.has(this.hostPeerIndex) &&
        !this._disconnected.has(this.hostPeerIndex) &&
        (eligible == null || eligible.has(this.hostPeerIndex))) {
      return;
    }
    var prev = this.hostPeerIndex;
    this.hostPeerIndex = this._electNextHost(this.hostPeerIndex);
    if (this.hostPeerIndex !== prev) this._emit('hostchange', { hostPeerIndex: this.host });
  };

  // =====================================================================
  // Lifecycle
  // =====================================================================

  // Snapshot the current connected roster as the active participant order
  // (join order). Called automatically when entering COUNTDOWN.
  RoomFlow.prototype._snapshotOrder = function () {
    var active = [];
    for (var entry of this.players) {
      if (!this._disconnected.has(entry[0])) active.push(entry[1]);
    }
    active.sort(function (a, b) { return a.joinedAt - b.joinedAt; });
    this._order = active.map(function (p) { return p.peerIndex; });
  };

  // Let a game that maintains its own participant order (e.g. for board
  // layout) keep RoomFlow's host-eligibility set exactly in sync with it.
  RoomFlow.prototype.setActiveOrder = function (peerIndices) {
    var out = [];
    for (var i = 0; i < (peerIndices || []).length; i++) {
      if (this.players.has(peerIndices[i])) out.push(peerIndices[i]);
    }
    this._order = out;
  };

  // Validated state transition. Public so games that run their own countdown
  // (like HexStacker) can drive the machine imperatively; the high-level
  // helpers below call it too. Returns true if applied.
  RoomFlow.prototype.transitionTo = function (to) {
    var from = this.state;
    if (to === from) return true;
    var allowed = VALID_TRANSITIONS[from];
    if (!allowed || allowed.indexOf(to) < 0) {
      if (typeof console !== 'undefined' && console.warn) console.warn('RoomFlow: invalid transition ' + from + ' -> ' + to);
      return false;
    }
    this.state = to;
    if (to !== STATES.PLAYING) this._graceDeadline = null;
    if (to === STATES.COUNTDOWN) this._snapshotOrder();
    if (to === STATES.LOBBY) this._order = [];
    if (to === STATES.LOBBY || to === STATES.RESULTS) this._reconcileStickyHost();
    this._emit('statechange', { from: from, to: to });
    return true;
  };

  // Lifecycle helpers — thin, validated transitions. The countdown itself is
  // game-owned (its visuals and controller messaging are game-flavored): a game
  // drives transitionTo(COUNTDOWN) -> run its own countdown -> transitionTo(PLAYING).

  // Readable sugar for `transitionTo('results')`. Results data is the game's
  // own (it knows the scoring); the kit does not store it.
  RoomFlow.prototype.endGame = function () {
    return this.transitionTo(STATES.RESULTS);
  };

  RoomFlow.prototype.returnToLobby = function () {
    return this.transitionTo(STATES.LOBBY);
  };

  // =====================================================================
  // Liveness (presence-timeout detection)
  // =====================================================================
  // All methods are pure, nowMs-injected predicates. They never mutate
  // _disconnected and never _emit; the host pulls the result and applies an
  // expiry through markDisconnected so hostchange still fires exactly once and
  // the single-writer invariant holds.

  // Record that we just heard from a peer (a controller message, a heartbeat,
  // a rejoin). Ignores unknown peers so a stray packet can't resurrect a stamp.
  RoomFlow.prototype.onSeen = function (peerIndex, nowMs) {
    if (this.players.has(peerIndex)) this._lastSeen.set(peerIndex, nowMs);
  };

  // Has this peer gone silent past the liveness window? Suppressed entirely
  // when enabledProvider() returns false (e.g. AirConsole, where the SDK's
  // connect/disconnect is authoritative and the relay PING is dropped). The
  // boundary is strict `>`: exactly-at-timeout is still considered alive. A peer
  // with no stamp (registered via peer_joined but never sent a message) is
  // treated as always-alive here; its only cleanup path is peer_left.
  RoomFlow.prototype.isExpired = function (peerIndex, nowMs) {
    if (this._livenessEnabledProvider && !this._livenessEnabledProvider()) return false;
    return this._lastSeen.has(peerIndex) &&
      (nowMs - this._lastSeen.get(peerIndex) > this._livenessTimeoutMs);
  };

  // Peers that just crossed the liveness window and are not already flagged
  // disconnected. Empty in LOBBY (a silent idle controller there is fine) and
  // empty when liveness is disabled (the enabledProvider no-op in isExpired).
  RoomFlow.prototype.expiredPeers = function (nowMs) {
    if (this.state === STATES.LOBBY) return [];
    var out = [];
    for (var id of this.players.keys()) {
      if (this._disconnected.has(id)) continue;
      if (this.isExpired(id, nowMs)) out.push(id);
    }
    return out;
  };

  // Are every active participant (the `_order`) currently disconnected? False
  // when there is no active order (e.g. lobby) so an empty game never auto-pauses.
  RoomFlow.prototype.allParticipantsDisconnected = function () {
    if (this._order.length === 0) return false;
    for (var i = 0; i < this._order.length; i++) {
      if (!this._disconnected.has(this._order[i])) return false;
    }
    return true;
  };

  // Any roster member who is not in the active participant order (joined after
  // the game started). They're who the grace window waits for.
  RoomFlow.prototype.hasLateJoiners = function () {
    for (var id of this.players.keys()) {
      if (this._order.indexOf(id) < 0) return true;
    }
    return false;
  };

  // Deadline-driven late-joiner grace. While PLAYING with every participant
  // gone but late joiners waiting, the first call arms a deadline at
  // nowMs + graceMs and returns false; later calls return true exactly once
  // when the deadline elapses (clearing it), false before. Any frame where the
  // condition no longer holds (a participant reconnected, state changed) clears
  // the deadline, so a reconnect implicitly cancels the return-to-lobby.
  RoomFlow.prototype.graceTick = function (nowMs) {
    if (this.state === STATES.PLAYING &&
        this.allParticipantsDisconnected() &&
        this.hasLateJoiners()) {
      // graceMs=0 still arms here and fires on the NEXT qualifying tick (deadline
      // == nowMs), i.e. one tick of delay rather than firing immediately.
      if (this._graceDeadline == null) { this._graceDeadline = nowMs + this._graceMs; return false; }
      if (nowMs >= this._graceDeadline) { this._graceDeadline = null; return true; }
      return false;
    }
    this._graceDeadline = null;
    return false;
  };

  // =====================================================================
  // Read accessors
  // =====================================================================

  // Roster as an array sorted by join order.
  RoomFlow.prototype.list = function () {
    var arr = [];
    for (var entry of this.players) arr.push(entry[1]);
    arr.sort(function (a, b) { return a.joinedAt - b.joinedAt; });
    return arr;
  };

  RoomFlow.prototype.get = function (peerIndex) { return this.players.get(peerIndex) || null; };
  RoomFlow.prototype.has = function (peerIndex) { return this.players.has(peerIndex); };

  Object.defineProperty(RoomFlow.prototype, 'size', {
    get: function () { return this.players.size; },
  });

  // Connected player count (what a lobby "Start (N)" button should show).
  Object.defineProperty(RoomFlow.prototype, 'connectedCount', {
    get: function () {
      var n = 0;
      for (var entry of this.players) { if (!this._disconnected.has(entry[0])) n++; }
      return n;
    },
  });

  RoomFlow.prototype.isDisconnected = function (peerIndex) { return this._disconnected.has(peerIndex); };

  // Reset to a fresh room (new room / return to welcome). Mirrors the
  // roster/host/state portion of DisplayState.resetRoomData.
  RoomFlow.prototype.reset = function () {
    var prevState = this.state;
    var hadHost = this.hostPeerIndex != null;
    // IMPORTANT: clear the Map in place — never reassign `this.players`.
    // Consumers may alias this exact Map object as their roster (HexStacker's
    // DisplayState does), so reassigning would leave them on a stale Map.
    this.players.clear();
    this._disconnected.clear();
    this._lastSeen.clear();
    this._graceDeadline = null;
    this._order = [];
    this.hostPeerIndex = null;
    this._joinSeq = 0;
    this.state = STATES.LOBBY;
    // Emit so event-driven consumers re-render on reset — every other state
    // change goes through transitionTo (which emits), so reset must too or a
    // game subscribed to statechange/rosterchange would miss the room wipe.
    if (prevState !== STATES.LOBBY) this._emit('statechange', { from: prevState, to: STATES.LOBBY });
    this._emit('rosterchange', { players: [] });
    if (hadHost) this._emit('hostchange', { hostPeerIndex: null });
  };

  return RoomFlow;
});

// ==== partyplug/PartyFastlane.js ====
'use strict';

/**
 * PartyFastlane — peer-to-peer DataChannel layer that piggybacks on an
 * existing relay/signal channel (e.g. PartyConnection).
 *
 * The lib doesn't own a WebSocket; signaling rides on the integrator's
 * `sendSignal` callback. Input events are resent from a short rolling window
 * until the peer cumulatively acks the highest applied event seq.
 *
 * Wire format (over the DataChannel):
 *
 *   data:       { ps, t, h: [ ev, ... ] }   // sender → peer
 *   heartbeat:  { ps, t, h: [] }            // sender → peer (idle)
 *   ack:        { pa, t }                   // peer ↩
 *
 *   ps  — sender's newest event seq carried by this packet (heartbeats
 *          keep the last data packet's ps, since no new event was added).
 *   t   — sender's clock at send time (echoed in the ack).
 *   h   — event payloads, newest first. Per-entry seq is implicit:
 *         es[i] = ps - i. Safe because the ring only prunes from the
 *         oldest end (TTL), so entries are always consecutive in seq.
 *   pa  — receiver's "highest event seq applied so far." Replaces both
 *         the prior `pa` (packet acked) and `ea` (event applied).
 *
 * Channel mode: { ordered: false, maxRetransmits: 0 }. SCTP retransmits
 * are off — app-layer redundancy + per-event seq dedup replaces them, with
 * tighter latency and no contention with SCTP's own retry timer.
 *
 * WATCHDOG_MS of inbound silence tears down the peer and surfaces
 * onPeerClosed so callers can fall back or update UI.
 *
 * Perfect negotiation: higher-indexed peer is polite (rolls back on
 * collision); lower-indexed peer is impolite. selfIndex (constructor option
 * or setSelfIndex) must be set before open() so the role is known.
 */
(function () {
  var RTC_KEY = '__rtc';

  // Netcode parameters.
  // TICK_MS:     resend cadence while ring has unacked events.
  // TTL_MS:      how long a re-sendable event stays in the ring (3× TICK_MS).
  // IDLE_MS:     heartbeat cadence when ring is empty.
  // WATCHDOG_MS: silence threshold before declaring the peer dead. 6× IDLE_MS
  //              so several transient missed heartbeats don't trigger teardown.
  // RTT_ALPHA:   exponential smoothing factor on the RTT estimator (Gaffer).
  var TICK_MS = 50;
  var TTL_MS = 300;
  var IDLE_MS = 500;
  var WATCHDOG_MS = 3000;
  var RTT_ALPHA = 0.1;

  function PartyFastlane(options) {
    options = options || {};
    this.iceServers = options.iceServers || [];
    this.selfIndex = options.selfIndex != null ? options.selfIndex : null;
    this.sendSignal = options.sendSignal || function () {};
    this.onInput = options.onInput || null;
    this.onPeerReady = options.onPeerReady || null;
    this.onPeerClosed = options.onPeerClosed || null;
    this.onConnectionState = options.onConnectionState || null;
    this.onRtt = options.onRtt || null;
    // Send-side role: when true, emit empty `{ ps, t, h: [] }` heartbeats
    // every IDLE_MS while the channel is open and the ring is drained.
    // Receiving side (display) leaves this false — it only emits acks.
    this.emitIdleHeartbeat = !!options.emitIdleHeartbeat;

    this.peers = new Map();

    // Per-peer instrumentation. See _statsFor() for shape and getStats() for
    // derived values. Counters intentionally outlive _teardownPeer — they
    // aggregate across reconnects so getStats(idx) returns lifetime totals
    // for that peer index, not per-session ones. Heartbeats don't advance
    // ps so they don't pollute the count.
    this._stats = new Map();
  }

  PartyFastlane.prototype.setSelfIndex = function (idx) {
    this.selfIndex = idx;
  };

  // Returns true if the message was an RTC envelope and was handled.
  PartyFastlane.prototype.handleSignal = function (from, data) {
    if (!data || typeof data !== 'object' || !(RTC_KEY in data)) return false;
    this._handleRtcSignal(from, data);
    return true;
  };

  PartyFastlane.prototype.open = async function (peerIdx, opts) {
    opts = opts || {};
    if (this.selfIndex == null) throw new Error('PartyFastlane: selfIndex not set');
    if (peerIdx === this.selfIndex) throw new Error("can't open a fastlane to self");
    if (typeof RTCPeerConnection === 'undefined') throw new Error('WebRTC not supported');

    var peer = this.peers.get(peerIdx);
    if (peer && peer.channel && peer.channel.readyState === 'open') return;
    if (!peer) peer = this._ensurePeer(peerIdx);

    var needOffer =
      !peer.makingOffer &&
      peer.pc.signalingState === 'stable' &&
      !peer.channel;

    if (needOffer) {
      // Unreliable + unordered: redundancy is app-layer (rolling window),
      // SCTP retransmits would compete with it on slow links.
      var channel = peer.pc.createDataChannel('party', {
        ordered: false,
        maxRetransmits: 0,
      });
      this._wireChannel(peer, peerIdx, channel);

      try {
        peer.makingOffer = true;
        var offer = await peer.pc.createOffer();
        await peer.pc.setLocalDescription(offer);
        this.sendSignal(peerIdx, { [RTC_KEY]: 'offer', sdp: peer.pc.localDescription });
      } finally {
        peer.makingOffer = false;
      }
    }

    if (opts.timeoutMs == null) return;
    return this._waitForOpen(peerIdx, opts.timeoutMs);
  };

  PartyFastlane.prototype._waitForOpen = function (peerIdx, timeoutMs) {
    var self = this;
    var peer = this.peers.get(peerIdx);
    if (peer && peer.channel && peer.channel.readyState === 'open') {
      return Promise.resolve();
    }
    return new Promise(function (resolve, reject) {
      // Re-read inside the executor: the outer `peer` lookup happens before
      // the Promise body runs, and the peer entry can change in between.
      var activePeer = self.peers.get(peerIdx);
      if (!activePeer) {
        reject(new Error('fastlane to ' + peerIdx + ' closed'));
        return;
      }
      var done = false;
      var settle = function (err) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        activePeer._waitResolvers = activePeer._waitResolvers
          .filter(function (r) { return r !== entry; });
        if (err) reject(err); else resolve();
      };
      var entry = { settle: settle };
      activePeer._waitResolvers.push(entry);

      var timer = setTimeout(function () {
        settle(new Error('fastlane to ' + peerIdx + ' timed out after ' + timeoutMs + 'ms'));
        self.close(peerIdx);
      }, timeoutMs);
    });
  };

  PartyFastlane.prototype.close = function (peerIdx) {
    this._teardownPeer(peerIdx);
  };

  PartyFastlane.prototype.closeAll = function () {
    var indices = [];
    this.peers.forEach(function (_v, k) { indices.push(k); });
    for (var i = 0; i < indices.length; i++) this._teardownPeer(indices[i]);
  };

  // Enqueue `ev` for delivery to `peerIdx`. Returns 'p2p' if the channel is
  // open and the event was added to the send ring, 'dropped' otherwise.
  // The actual send is automatic — the event sits in the ring until it's
  // acked or expires (TTL_MS). Resend ticks fire every TICK_MS while the
  // ring is non-empty; ack from the peer clears acknowledged events.
  PartyFastlane.prototype.enqueue = function (peerIdx, ev) {
    var peer = this.peers.get(peerIdx);
    if (!peer || !peer.channel || peer.channel.readyState !== 'open') {
      return 'dropped';
    }
    peer.eventSeq += 1;
    peer.ring.unshift({
      es: peer.eventSeq,
      ev: ev,
      expires: Date.now() + TTL_MS,
    });
    this._sendDataPacket(peer, peerIdx);
    return 'p2p';
  };

  PartyFastlane.prototype.isOpen = function (peerIdx) {
    var peer = this.peers.get(peerIdx);
    return !!(peer && peer.channel && peer.channel.readyState === 'open');
  };

  // Per-peer packet counters. Minimal surface — the only thing the lib
  // genuinely tracks for diagnostics. RTT is surfaced via the onRtt
  // callback; anything else (ring depth, applied/acked event seq) lives
  // on the peer object and is internal state.
  //   out          packets sent to this peer (data + heartbeats + acks)
  //   received     packets received from this peer
  //   lastPsSeen   highest inbound ps observed (= peer's max event seq)
  PartyFastlane.prototype.getStats = function (peerIdx) {
    var s = this._stats.get(peerIdx);
    if (!s) return null;
    return {
      out: s.out,
      received: s.received,
      lastPsSeen: s.lastPsSeen,
    };
  };

  PartyFastlane.prototype.getAllStats = function () {
    var out = {};
    var self = this;
    this._stats.forEach(function (_v, k) { out[k] = self.getStats(k); });
    return out;
  };

  PartyFastlane.prototype._statsFor = function (peerIdx) {
    var s = this._stats.get(peerIdx);
    if (!s) {
      s = { out: 0, received: 0, lastPsSeen: 0 };
      this._stats.set(peerIdx, s);
    }
    return s;
  };

  // --- send path ---

  PartyFastlane.prototype._sendDataPacket = function (peer, peerIdx) {
    var now = Date.now();
    // Age out expired events. The ring is newest-first (unshift) and TTL is
    // monotonic in enqueue order, so expired entries are a strict suffix —
    // truncate it in place (this runs every TICK_MS; no per-tick array
    // allocation). This is what makes implicit seq encoding safe — receivers
    // can compute es[i] = ps - i without an explicit per-entry seq field.
    var liveLen = peer.ring.length;
    while (liveLen > 0 && peer.ring[liveLen - 1].expires <= now) liveLen--;
    if (liveLen < peer.ring.length) peer.ring.length = liveLen;

    if (peer.ring.length === 0) {
      if (peer.sendTimer) { clearTimeout(peer.sendTimer); peer.sendTimer = null; }
      this._resetIdleTimer(peer, peerIdx);
      return;
    }

    // ps == newest event's es. Heartbeats reuse this value (see below);
    // resends reuse it too (same packet content, same ps), which is fine
    // because receivers dedupe per event via lastAppliedEs.
    this._writeRaw(peer, peerIdx, {
      ps: peer.eventSeq,
      t: now,
      h: peer.ring.map(function (e) { return e.ev; }),
    });

    if (peer.sendTimer) clearTimeout(peer.sendTimer);
    var self = this;
    peer.sendTimer = setTimeout(function () {
      self._sendDataPacket(peer, peerIdx);
    }, TICK_MS);
    if (peer.idleTimer) { clearTimeout(peer.idleTimer); peer.idleTimer = null; }
  };

  PartyFastlane.prototype._resetIdleTimer = function (peer, peerIdx) {
    if (!this.emitIdleHeartbeat) return;
    if (!peer.channel || peer.channel.readyState !== 'open') return;
    if (peer.idleTimer) clearTimeout(peer.idleTimer);
    var self = this;
    peer.idleTimer = setTimeout(function () {
      self._sendIdleHeartbeat(peer, peerIdx);
    }, IDLE_MS);
  };

  PartyFastlane.prototype._sendIdleHeartbeat = function (peer, peerIdx) {
    if (!peer.channel || peer.channel.readyState !== 'open') return;
    if (peer.ring.length > 0) {
      // A real send happened between scheduling and firing — defer.
      this._resetIdleTimer(peer, peerIdx);
      return;
    }
    // ps unchanged: same value as the most recent data packet (or 0 if
    // none yet). Heartbeat exists purely to refresh `t` for RTT samples.
    this._writeRaw(peer, peerIdx, {
      ps: peer.eventSeq,
      t: Date.now(),
      h: [],
    });
    this._resetIdleTimer(peer, peerIdx);
  };

  PartyFastlane.prototype._sendAck = function (peer, peerIdx, dataPacket) {
    if (!peer.channel || peer.channel.readyState !== 'open') return;
    this._writeRaw(peer, peerIdx, {
      pa: peer.lastAppliedEs,
      t: dataPacket.t,
    });
  };

  PartyFastlane.prototype._writeRaw = function (peer, peerIdx, packet) {
    try {
      peer.channel.send(JSON.stringify(packet));
      this._statsFor(peerIdx).out += 1;
    } catch (_) { /* channel transitioned; next caller will see closed state */ }
  };

  // --- receive path ---

  PartyFastlane.prototype._handleDataPacket = function (peer, peerIdx, packet) {
    if (typeof packet.ps !== 'number') return;
    var h = Array.isArray(packet.h) ? packet.h : [];
    // Events arrive newest first; iterate oldest-first so onInput receives
    // them in source order. es is implicit: es[i] = ps - i.
    for (var i = h.length - 1; i >= 0; i--) {
      var es = packet.ps - i;
      if (es > peer.lastAppliedEs) {
        peer.lastAppliedEs = es;
        if (this.onInput) this.onInput(peerIdx, h[i]);
      }
    }
    // Always ack — even duplicates and heartbeats. Re-acks are idempotent
    // on this side and give the sender another shot at clearing its ring
    // if the prior ack was lost.
    this._sendAck(peer, peerIdx, packet);
  };

  PartyFastlane.prototype._handleAck = function (peer, peerIdx, ack) {
    if (typeof ack.pa === 'number' && ack.pa > peer.lastAckedEs) {
      peer.lastAckedEs = ack.pa;
      // pa is cumulative-highest-applied and the ring is newest-first, so the
      // acked entries (es <= pa) are a strict suffix — truncate it in place,
      // leaving the contiguous unacked head. This is what lets the receiver
      // decode es[i] = ps - i without an explicit per-entry seq field.
      var keepLen = peer.ring.length;
      while (keepLen > 0 && peer.ring[keepLen - 1].es <= ack.pa) keepLen--;
      if (keepLen < peer.ring.length) peer.ring.length = keepLen;
      if (peer.ring.length === 0 && peer.sendTimer) {
        clearTimeout(peer.sendTimer);
        peer.sendTimer = null;
        this._resetIdleTimer(peer, peerIdx);
      }
    }
    if (typeof ack.t === 'number') {
      var rtt = Date.now() - ack.t;
      // Discard wild samples (clock jumps, very late acks). A 500 ms cap is
      // tight enough that a stale ack carrying a 400 ms RTT can't shift a
      // healthy srtt of 20 ms more than ~38 ms via the EWMA — and the
      // chip recovers within a few subsequent good samples.
      if (rtt >= 0 && rtt < 500) {
        if (peer.srtt === 0) peer.srtt = rtt;
        else peer.srtt = peer.srtt + (rtt - peer.srtt) * RTT_ALPHA;
        if (this.onRtt) this.onRtt(peerIdx, peer.srtt / 2);
      }
    }
  };

  // --- watchdog ---
  // Each side declares the peer dead if no inbound packet arrives for
  // WATCHDOG_MS. On the controller this fires when acks stop coming back
  // (channel is silently broken); on the display, when heartbeats stop
  // arriving. The timer is reset on every inbound packet — if WATCHDOG_MS
  // elapses without a reset, the timer fires and teardown runs.

  PartyFastlane.prototype._resetWatchdog = function (peer, peerIdx) {
    if (peer.watchdogTimer) clearTimeout(peer.watchdogTimer);
    var self = this;
    peer.watchdogTimer = setTimeout(function () {
      console.warn('[fastlane] watchdog: no inbound from peer', peerIdx, 'in', WATCHDOG_MS, 'ms — tearing down');
      self._teardownPeer(peerIdx);
    }, WATCHDOG_MS);
  };

  // --- internals: peer + signaling ---

  PartyFastlane.prototype._ensurePeer = function (peerIdx) {
    var existing = this.peers.get(peerIdx);
    if (existing) return existing;

    var pc = new RTCPeerConnection({ iceServers: this.iceServers });
    var polite = this.selfIndex > peerIdx;
    var peer = {
      pc: pc,
      channel: null,
      pendingCandidates: [],
      polite: polite,
      makingOffer: false,
      ignoreOffer: false,
      _waitResolvers: [],
      eventSeq: 0,        // monotonic event seq for this peer's outbound stream
      ring: [],
      sendTimer: null,
      idleTimer: null,
      watchdogTimer: null,
      lastAckedEs: 0,     // sender side: events this peer has confirmed applied
      lastAppliedEs: 0,   // receiver side: events we've applied from this peer
      srtt: 0,
    };
    this.peers.set(peerIdx, peer);

    var self = this;
    pc.onicecandidate = function (ev) {
      // Browsers fire onicecandidate with ev.candidate === null when ICE
      // gathering completes. Forwarding the null is technically valid
      // (end-of-candidates marker) but burns a WS message no implementation
      // here acts on. Skip it.
      if (!ev.candidate) return;
      self.sendSignal(peerIdx, { [RTC_KEY]: 'ice', candidate: ev.candidate });
    };
    pc.ondatachannel = function (ev) {
      self._wireChannel(peer, peerIdx, ev.channel);
    };
    pc.onconnectionstatechange = function () {
      var state = pc.connectionState;
      if (self.onConnectionState) self.onConnectionState(peerIdx, state);
      if (state === 'failed' || state === 'closed') {
        self._teardownPeer(peerIdx);
      }
    };

    return peer;
  };

  PartyFastlane.prototype._wireChannel = function (peer, peerIdx, channel) {
    if (peer.channel && peer.channel !== channel) {
      // Orphan from a rolled-back offer; detach handlers but don't close()
      // (shared SCTP stream id would tear down the adopted channel remotely).
      peer.channel.onopen = null;
      peer.channel.onmessage = null;
      peer.channel.onclose = null;
      peer.channel.onerror = null;
    }
    peer.channel = channel;

    var self = this;
    channel.onopen = function () {
      if (self.onPeerReady) self.onPeerReady(peerIdx);
      var waiters = peer._waitResolvers;
      peer._waitResolvers = [];
      for (var i = 0; i < waiters.length; i++) waiters[i].settle();
      // Start idle heartbeat schedule (sender side). First enqueue cancels
      // and switches to the data send loop.
      self._resetIdleTimer(peer, peerIdx);
      self._resetWatchdog(peer, peerIdx);
    };
    channel.onmessage = function (e) {
      var parsed;
      try { parsed = JSON.parse(e.data); } catch (_) { return; }
      if (!parsed || typeof parsed !== 'object') return;

      // Any well-formed packet refreshes the watchdog.
      self._resetWatchdog(peer, peerIdx);

      var s = self._statsFor(peerIdx);
      s.received += 1;
      // Data packets advance lastPsSeen; acks don't carry ps (they have pa).
      if (typeof parsed.ps === 'number' && parsed.ps > s.lastPsSeen) {
        s.lastPsSeen = parsed.ps;
      }

      // Disambiguate: presence of `pa` → ack; presence of `h` → data
      // (heartbeats are data packets with h: []).
      if ('pa' in parsed) {
        self._handleAck(peer, peerIdx, parsed);
      } else if ('h' in parsed) {
        self._handleDataPacket(peer, peerIdx, parsed);
      }
    };
    channel.onclose = function () {
      // Orphan channels from glare rollback should not trigger teardown of
      // the adopted channel. Route through _teardownPeer so onPeerClosed
      // fires exactly once (the watchdog and the pc connectionState path
      // also funnel through there; _teardownPeer is idempotent).
      if (peer.channel !== channel) return;
      self._teardownPeer(peerIdx);
    };
    channel.onerror = function () { /* surfaced via onclose / state change */ };
  };

  PartyFastlane.prototype._handleRtcSignal = async function (from, data) {
    if (this.selfIndex == null) return;
    var peer = this._ensurePeer(from);
    var pc = peer.pc;
    var kind = data[RTC_KEY];

    try {
      if (kind === 'offer' || kind === 'answer') {
        var isOffer = kind === 'offer';
        var collision = isOffer &&
          (peer.makingOffer || pc.signalingState !== 'stable');
        peer.ignoreOffer = !peer.polite && collision;
        if (peer.ignoreOffer) return;

        await pc.setRemoteDescription(data.sdp);
        for (var i = 0; i < peer.pendingCandidates.length; i++) {
          try { await pc.addIceCandidate(peer.pendingCandidates[i]); } catch (_) {}
        }
        peer.pendingCandidates.length = 0;

        if (isOffer) {
          var answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          this.sendSignal(from, { [RTC_KEY]: 'answer', sdp: pc.localDescription });
        }
      } else if (kind === 'ice') {
        var c = data.candidate;
        if (!pc.remoteDescription) {
          peer.pendingCandidates.push(c);
          return;
        }
        try {
          await pc.addIceCandidate(c);
        } catch (e) {
          if (!peer.ignoreOffer) console.warn('[fastlane] addIceCandidate failed', e);
        }
      }
    } catch (err) {
      console.warn('[fastlane] signal handling failed', err);
    }
  };

  PartyFastlane.prototype._teardownPeer = function (peerIdx) {
    var peer = this.peers.get(peerIdx);
    if (!peer) return;
    this.peers.delete(peerIdx);

    if (peer.sendTimer) clearTimeout(peer.sendTimer);
    if (peer.idleTimer) clearTimeout(peer.idleTimer);
    if (peer.watchdogTimer) clearTimeout(peer.watchdogTimer);

    var waiters = peer._waitResolvers;
    peer._waitResolvers = [];
    for (var i = 0; i < waiters.length; i++) {
      waiters[i].settle(new Error('fastlane to ' + peerIdx + ' closed'));
    }
    try { if (peer.channel) peer.channel.close(); } catch (_) {}
    try { peer.pc.close(); } catch (_) {}

    if (this.onPeerClosed) this.onPeerClosed(peerIdx);
  };

  if (typeof window !== 'undefined') window.PartyFastlane = PartyFastlane;
  if (typeof module !== 'undefined' && module.exports) module.exports = PartyFastlane;
})();

